import type { CSSProperties } from "react";
import { ButtonLink } from "@/shared/components/ButtonLink";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

/**
 * Full-screen celebration shown after an invite is accepted (design handoff 5a).
 * Pure-CSS timeline (badge pop → check draw → ring pulse → diamond confetti →
 * text/CTA rise) defined in `styles.css` as `invite-*` classes, which also carry
 * the `prefers-reduced-motion` fallback (final state, no motion). Colours use the
 * app tokens: gold `--accent-500`, dark gold `--accent-600`, navy check on the
 * `--color-hero-surface` panel.
 */

const GREETING_FONT: CSSProperties = {
  fontFamily: "'Tajawal', 'IBM Plex Sans Arabic', system-ui, sans-serif",
};

const GOLD = "var(--accent-500)";
const GOLD_DARK = "var(--accent-600)";
const CREAM = "var(--canvas)";

type Particle = {
  size: number;
  color: string;
  dx: number;
  dy: number;
  delay: number;
  duration: number;
};

const DESKTOP_CONFETTI: Particle[] = [
  { size: 14, color: GOLD, dx: -150, dy: -120, delay: 0.55, duration: 1.1 },
  { size: 10, color: CREAM, dx: 140, dy: -140, delay: 0.6, duration: 1.2 },
  { size: 12, color: GOLD_DARK, dx: 180, dy: -40, delay: 0.58, duration: 1.05 },
  { size: 9, color: GOLD, dx: -190, dy: -20, delay: 0.62, duration: 1.15 },
  { size: 11, color: CREAM, dx: -110, dy: 130, delay: 0.65, duration: 1.1 },
  { size: 10, color: GOLD_DARK, dx: 120, dy: 120, delay: 0.6, duration: 1.2 },
  { size: 8, color: GOLD, dx: 30, dy: -180, delay: 0.57, duration: 1.25 },
  { size: 8, color: CREAM, dx: -40, dy: 170, delay: 0.63, duration: 1.15 },
];

const MOBILE_CONFETTI: Particle[] = [
  { size: 12, color: GOLD, dx: -110, dy: -95, delay: 0.55, duration: 1.1 },
  { size: 9, color: CREAM, dx: 105, dy: -110, delay: 0.6, duration: 1.2 },
  { size: 10, color: GOLD_DARK, dx: 130, dy: -30, delay: 0.58, duration: 1.05 },
  { size: 8, color: GOLD, dx: -140, dy: -15, delay: 0.62, duration: 1.15 },
  { size: 9, color: CREAM, dx: -85, dy: 100, delay: 0.65, duration: 1.1 },
  { size: 9, color: GOLD_DARK, dx: 90, dy: 95, delay: 0.6, duration: 1.2 },
  { size: 7, color: GOLD, dx: 20, dy: -140, delay: 0.57, duration: 1.25 },
];

function Burst({ badge, particles }: { badge: number; particles: Particle[] }) {
  const check = Math.round(badge * 0.46);
  return (
    <div className="relative" style={{ width: badge, height: badge }}>
      <span className="invite-ring absolute inset-0 rounded-full border-2 border-[color:var(--accent-500)]" />
      {particles.map((p, i) => (
        <span
          key={i}
          className="invite-confetti absolute start-1/2 top-1/2"
          style={
            {
              width: p.size,
              height: p.size,
              margin: -p.size / 2,
              background: p.color,
              "--dx": `${p.dx}px`,
              "--dy": `${p.dy}px`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            } as CSSProperties
          }
        />
      ))}
      <div
        className="invite-badge absolute inset-0 flex items-center justify-center rounded-full bg-[color:var(--accent-500)]"
        style={{ boxShadow: "0 16px 44px rgba(250, 185, 91, 0.35)" }}
      >
        <svg width={check} height={check} viewBox="0 0 48 48" fill="none" aria-hidden="true">
          <path
            d="M12 25 L20 33 L36 16"
            stroke="var(--color-hero-surface)"
            strokeWidth={5}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={48}
            className="invite-check"
          />
        </svg>
      </div>
    </div>
  );
}

export function InviteSuccessScreen({ loginHref }: { loginHref: string }) {
  const t = useTranslations();
  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-[color:var(--color-hero-surface)] px-7 py-12 text-center text-[color:var(--color-hero-ink)]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute rotate-45"
        style={{ insetInlineEnd: -140, top: -180, width: 460, height: 460, background: "rgba(250, 185, 91, 0.07)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute rotate-45"
        style={{ insetInlineStart: -160, bottom: -200, width: 440, height: 440, background: "rgba(250, 185, 91, 0.05)" }}
      />

      <div className="relative flex max-w-[560px] flex-col items-center gap-6 lg:gap-7">
        <div className="lg:hidden">
          <Burst badge={104} particles={MOBILE_CONFETTI} />
        </div>
        <div className="hidden lg:block">
          <Burst badge={120} particles={DESKTOP_CONFETTI} />
        </div>

        <div className="invite-rise flex flex-col gap-3" style={{ animationDelay: "0.9s" }}>
          <span
            className="text-[16px] font-semibold text-[color:var(--accent-500)] lg:text-[19px]"
            style={GREETING_FONT}
          >
            {t(keys.invite.successGreeting)}
          </span>
          <h1 className="m-0 text-[28px] font-bold leading-[1.2] tracking-[-0.02em] lg:text-[42px] lg:leading-[1.1]">
            {t(keys.invite.successTitle)}
          </h1>
          <p className="m-0 text-[15px] leading-relaxed text-[color:var(--color-hero-ink-subtle)] lg:text-[17px]">
            {t(keys.invite.successBody)}
          </p>
        </div>

        <div
          className="invite-rise flex w-full flex-col items-center gap-3.5"
          style={{ animationDelay: "1.15s" }}
        >
          <ButtonLink
            href={loginHref}
            variant="secondary"
            size="lg"
            className="h-[54px] w-full max-w-[300px] rounded-[10px] text-[16px] font-bold"
          >
            <span>{t(keys.invite.successCta)}</span>
            <ArrowRightIcon />
          </ButtonLink>
          <span className="text-[13px] text-[color:var(--sidebar-ink-disabled)]">
            {t(keys.invite.successFinePrint)}
          </span>
        </div>
      </div>
    </div>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="16"
      height="16"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
